﻿import {currentCulture, DateTime, DayOfWeek, NamedValue, TimeOnly} from "@2d-front-monorepo/core";
import {MvvmOptionGroupEntry} from "@2d-front-monorepo/design-library";
import {BasePermitRecurrence, ModifiedProperty, RecurrentPermitOptions} from "./BasePermitRecurrence";

export interface RecurrenceWeeklyOptions extends RecurrentPermitOptions {
}

export class RecurrenceWeekly extends BasePermitRecurrence {
	private _daysOfWeek: DayOfWeek[];
	private _availableDaysOfWeek: MvvmOptionGroupEntry<DayOfWeek>[] | undefined;
	private _availableStartTimes: NamedValue<TimeOnly>[] | undefined;
	private _availableEndTimes: NamedValue<TimeOnly>[] | undefined;

	constructor(options: RecurrenceWeeklyOptions) {
		super(options);
		this._daysOfWeek = [];
	}

	public override getAvailableStartTimes(): NamedValue<TimeOnly>[] {
		if (this._availableStartTimes != null) {
			return this._availableStartTimes;
		}

		return this._availableStartTimes = this._getAvailableTimes(false);
	}

	public override getAvailableEndTimes(): NamedValue<TimeOnly>[] {
		if (this._availableEndTimes != null) {
			return this._availableEndTimes;
		}

		return this._availableEndTimes = this._getAvailableTimes(true);
	}

	public override isValidEndDate(): boolean {
		return super.isValidEndDate() && this._operations.size > 1;
	}

	public override isValid(): boolean {
		return super.isValid() && this._operations.size > 1;
	}

	public get daysOfWeek(): DayOfWeek[] {
		return this._daysOfWeek;
	}

	public set daysOfWeek(daysOfWeek: DayOfWeek[]) {
		this._daysOfWeek = daysOfWeek;
		this._availableDaysOfWeek = undefined;
		this._createSegments(ModifiedProperty.DayOfWeek);
	}

	public getDaysOfWeeks(): string {
		const dayNames = currentCulture().shortDayNames;
		const sortedDays = [...this.daysOfWeek].sort((a, b) => a - b);
		const result: string[] = [];
		for (const sortedDay of sortedDays) {
			result.push(dayNames[sortedDay].label);
		}

		return result.join(" - ");
	}

	public getAvailableDaysOfWeek(): MvvmOptionGroupEntry<DayOfWeek>[] {
		if (this._availableDaysOfWeek != null) {
			return this._availableDaysOfWeek;
		}

		const dayNames = currentCulture().shortDayNames;
		const sortedDays = [...this._daysOfWeek].sort((a, b) => a - b);
		const maxConsecutiveDays = this._maxConsecutiveDays;

		const isDisabled = (day: DayOfWeek): boolean => {
			if (sortedDays.includes(day)) {
				return false;
			}
			return this._shouldDisable(day, sortedDays, maxConsecutiveDays);
		};

		return this._availableDaysOfWeek = [
			{
				label: dayNames[1].label,
				value: DayOfWeek.Monday,
				disabled: isDisabled(DayOfWeek.Monday)
			},
			{
				label: dayNames[2].label,
				value: DayOfWeek.Tuesday,
				disabled: isDisabled(DayOfWeek.Tuesday)
			},
			{
				label: dayNames[3].label,
				value: DayOfWeek.Wednesday,
				disabled: isDisabled(DayOfWeek.Wednesday)
			},
			{
				label: dayNames[4].label,
				value: DayOfWeek.Thursday,
				disabled: isDisabled(DayOfWeek.Thursday)
			},
			{
				label: dayNames[5].label,
				value: DayOfWeek.Friday,
				disabled: isDisabled(DayOfWeek.Friday)
			},
			{
				label: dayNames[6].label,
				value: DayOfWeek.Saturday,
				disabled: isDisabled(DayOfWeek.Saturday)
			},
			{
				label: dayNames[0].label,
				value: DayOfWeek.Sunday,
				disabled: isDisabled(DayOfWeek.Sunday)
			}
		];
	}

	protected override _createSegments(property: ModifiedProperty): void {
		const daysOfWeek = this._daysOfWeek;
		const operations = this._operations;
		operations.clear();

		if (daysOfWeek.length === 0) {
			return;
		}

		let recurrenceStartDate = this._startDate;
		const recurrenceEndDate = this._endDate;

		while (recurrenceStartDate <= recurrenceEndDate) {
			if (daysOfWeek.includes(recurrenceStartDate.getDayOfWeek())) {
				const rs = DateTime.fromDateAndTime(recurrenceStartDate, this._startTime);
				const re = DateTime.fromDateAndTime(recurrenceStartDate, this._endTime);

				if (!operations.tryCreateSegment(rs, re)) {
					operations.clear();
					return;
				}
			}

			recurrenceStartDate = recurrenceStartDate.addDays(1);
		}
	}

	private _getAvailableTimes(endDay: boolean): NamedValue<TimeOnly>[] {
		const culture = currentCulture();
		const result: NamedValue<TimeOnly>[] = [];

		for (let i = endDay ? 1 : 0; i <= 23; i++) {
			const time = TimeOnly.fromComponents(i, 0, 0);
			result.push({label: culture.formatTime(time, "short-time"), value: time});
		}

		if (endDay) {
			const time = TimeOnly.fromComponents(23, 59, 59);
			result.push({label: culture.formatTime(time, "short-time"), value: time});
		}

		return result;
	}

	private _getNextValidDay = (d: DayOfWeek): DayOfWeek => {
		return (d + 1) % 7;
	}

	private _areConsecutive = (d1: DayOfWeek, d2: DayOfWeek): boolean => {
		return this._getNextValidDay(d1) === d2;
	}

	private _shouldDisable(day: DayOfWeek, sorted: DayOfWeek[], maxConsecutiveDays: number): boolean {
		if (sorted.length === 0) {
			return false;
		}

		const newSortedDays = [...sorted, day].sort((a, b) => a - b);
		let maxStreak = 1;

		let currentStreak = 1;
		for (let i = 1; i < newSortedDays.length; i++) {
			if (this._areConsecutive(newSortedDays[i - 1], newSortedDays[i])) {
				currentStreak++;
			} else {
				maxStreak = Math.max(maxStreak, currentStreak);
				currentStreak = 1;
			}
		}
		maxStreak = Math.max(maxStreak, currentStreak);

		if (newSortedDays.length > 1 && this._areConsecutive(newSortedDays[newSortedDays.length - 1], newSortedDays[0])) {
			let endStreak = 1;
			for (let i = newSortedDays.length - 2; i >= 0; i--) {
				if (this._areConsecutive(newSortedDays[i], newSortedDays[i + 1])) {
					endStreak++;
				} else {
					break;
				}
			}

			let startStreak = 1;
			for (let i = 1; i < newSortedDays.length; i++) {
				if (this._areConsecutive(newSortedDays[i - 1], newSortedDays[i])) {
					startStreak++;
				} else {
					break;
				}
			}

			const wrappedStreak = startStreak + endStreak;
			maxStreak = Math.max(maxStreak, wrappedStreak);
		}

		return maxStreak > maxConsecutiveDays;
	}
}
